import { UPSERT_BUYERS_BULK } from "@lib/graphql/outreach";
import { useState } from "react";
import { useMutation } from "urql";

interface Props {
  prospectCompanyId: string;
  onSaved?: () => void;
}

export function AddBuyersForm({ prospectCompanyId, onSaved }: Props) {
  const [, upsertBulk] = useMutation(UPSERT_BUYERS_BULK);
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [added, setAdded] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const list = urls
      .split(/\r?\n/)
      .map((u) => u.trim())
      .filter(Boolean);
    if (list.length === 0) return;

    setBusy(true);
    setErrors([]);
    setAdded(null);

    const result = await upsertBulk({
      input: { prospectCompanyId, linkedinUrls: list },
    });
    const payload = result.data?.upsertBuyersBulk;
    if (payload) {
      setErrors(payload.errors ?? []);
      setAdded(payload.buyers?.length ?? 0);
      if ((payload.errors?.length ?? 0) === 0) setUrls("");
    }
    onSaved?.();
    setBusy(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <label className="block text-xs font-medium text-text-secondary">
        LinkedIn profile URLs (one per line)
      </label>
      <textarea
        value={urls}
        onChange={(e) => setUrls(e.target.value)}
        rows={3}
        placeholder="https://www.linkedin.com/in/..."
        className="block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm font-mono"
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !urls.trim()}
          className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? "Adding…" : "Add buyers"}
        </button>
        {added !== null && errors.length === 0 && (
          <span className="text-xs text-text-muted">Added {added} buyers</span>
        )}
      </div>
      {errors.length > 0 && (
        <ul className="text-xs text-red-600 dark:text-red-400">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </form>
  );
}
